import {
    FC,
    ReactElement,
    FormEvent,
    useState,
    useMemo,
    useEffect,
    useCallback,
} from "react";
import useInputs from "hooks/useInputs";

import SearchBar from "components/searchbar";
import DataBox from "components/data-box";
import Map from "components/map";

import {Box, Flex, Heading} from "@chakra-ui/layout";

const API_KEY: string | undefined = process.env.REACT_APP_API_KEY;
const BASE_URL: string = `https://geo.ipify.org/api/v2/country,city?apiKey=${API_KEY}`;

type InitialValue = {domain: string};

const Homepage: FC = (): ReactElement => {
    const initialValue: InitialValue = useMemo(() => ({domain: ""}), []);
    const {inputs, handleChange, reset} = useInputs(initialValue);
    const [urlData, setUrlData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);

    const isValidUrl = (string: string) => {
        try {
            const url: URL = new URL(string);
            return url.protocol === "http:" || url.protocol === "https:";
        } catch (_) {
            return false;
        }
    };

    const includeWWW = (string: string) => {
        if (string.includes("www.")) {
            return true;
        }
        return false;
    };

    const checkExtensionsDomain: (string: string) => boolean = string => {
        const expression: RegExp = /\.[a-zA-Z]+$/i;
        const regex: RegExp = new RegExp(expression);

        let result: boolean = false;

        if (string.match(regex)) {
            const extensions: string[] = [
                ".com",
                ".org",
                ".io",
                ".net",
                ".be",
                ".fr",
                ".lu",
            ];
            for (const ex of extensions) {
                if (string.includes(ex)) {
                    result = true;
                    break;
                }
            }
        }
        return result;
    };

    const handleSubmit: (e: FormEvent) => void = useCallback(
        e => {
            e.preventDefault();

            if (inputs.domain) {
                setIsLoading(true);

                const domain: string = inputs.domain
                    .toLowerCase()
                    .trim()
                    .replace(/\s+/g, "");

                let params: string = `&ipAddress=${domain}`;

                if (isValidUrl(domain)) {
                    const {hostname} = new URL(domain);
                    params = `&domain=${hostname}`;
                } else if (includeWWW(domain)) {
                    params = `&domain=${domain.replace("www.", "")}`;
                } else if (checkExtensionsDomain(domain)) {
                    params = `&domain=${domain}`;
                }

                const url: string = BASE_URL + params;

                fetch(url)
                    .then(r => {
                        if (r.status === 200) {
                            return r.json();
                        }
                        throw new Error("Error");
                    })
                    .then(data => {
                        setUrlData(data);
                        setError(false);
                    })
                    .catch(() => {
                        setError(true);
                        setUrlData(null);
                    })
                    .finally(() => setIsLoading(false));

                reset();
            }
        },
        [reset, inputs.domain],
    );

    useEffect(() => {
        setIsLoading(true);

        fetch(BASE_URL)
            .then(r => {
                if (r.status === 200) {
                    return r.json();
                }
                throw new Error("Error");
            })
            .then(data => {
                setUrlData(data);
                setError(false);
            })
            .catch(() => {
                setError(true);
                setUrlData(null);
            })
            .finally(() => setIsLoading(false));
    }, []);

    return (
        <Box as={"main"} h={"100vh"}>
            <Flex
                h={{base: "42%", sm: "35%"}}
                flexDir={"column"}
                alignItems={"center"}
                bgImg={`${process.env.PUBLIC_URL}/images/pattern-bg.png`}
                bgSize={"cover"}
                position={"relative"}
            >
                <Heading
                    as={"h1"}
                    color={"#fff"}
                    mt={"1.5rem"}
                    fontSize={"1.5rem"}
                >
                    {"IP Address Tracker"}
                </Heading>
                <SearchBar
                    inputs={inputs}
                    handleSubmit={handleSubmit}
                    handleChange={handleChange}
                />
                <DataBox data={urlData} error={error} isLoading={isLoading} />
            </Flex>
            <Box h={"65%"}>
                <Map data={urlData} />
            </Box>
        </Box>
    );
};

export default Homepage;
